import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../services/api";
import RefreshButton from "../RefreshButton/RefreshButton";

function formatNumber(n) {
  if (n === null || n === undefined || n === "") return "0";
  const v = typeof n === "number" ? n : parseInt(n, 10);
  return Number.isFinite(v) ? v.toLocaleString() : "0";
}

function copyJson(obj) {
  try {
    navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
  } catch {
    /* no-op */
  }
}

function CollapsibleBlock({ title, defaultOpen = false, stat, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="prompt-section" style={{ marginBottom: "0.75rem" }}>
      <div
        className="section-header"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((v) => !v)}
      >
        <h4>{open ? "▾" : "▸"} {title}</h4>
        {stat && <span className="section-stat">{stat}</span>}
      </div>
      {open && children}
    </div>
  );
}

function ActionResultCard({ entry, idx }) {
  const ok = entry.success !== false && !entry.error;
  return (
    <div className={`variable-item ${ok ? "" : "empty"}`} style={{ marginBottom: "0.5rem" }}>
      <div className="variable-name">
        <span className="variable-label">
          {ok ? "✅" : "❌"} #{idx + 1} · {entry.action || "(unknown)"}
        </span>
        {typeof entry.durationMs === "number" && (
          <code className="variable-key">{entry.durationMs} ms</code>
        )}
      </div>
      {entry.args && Object.keys(entry.args).length > 0 && (
        <>
          <div className="variable-stats">args</div>
          <pre className="variable-value">{JSON.stringify(entry.args, null, 2)}</pre>
        </>
      )}
      {entry.result !== undefined && entry.result !== null && (
        <>
          <div className="variable-stats">result</div>
          <pre className="variable-value">
            {typeof entry.result === "string"
              ? entry.result
              : JSON.stringify(entry.result, null, 2)}
          </pre>
        </>
      )}
      {entry.error && (
        <>
          <div className="variable-stats" style={{ color: "#c33" }}>error</div>
          <pre className="variable-value" style={{ color: "#c33" }}>{entry.error}</pre>
        </>
      )}
    </div>
  );
}

function PassCard({ pass }) {
  const m = pass.metrics || {};
  const stat = [
    `pass #${pass.passNumber}`,
    pass.trigger,
    m.llmLatencyMs != null ? `${m.llmLatencyMs} ms` : null,
    m.outputTokensEstimate != null ? `~out ${formatNumber(m.outputTokensEstimate)}` : null,
    m.finishReason,
  ]
    .filter(Boolean)
    .join(" · ");

  const emitted = pass.actionsEmitted || [];
  const results = pass.actionsResults || [];

  return (
    <div
      className="prompt-log-detail"
      style={{ border: "1px solid #ccd", borderRadius: 6, padding: "0.75rem", marginBottom: "0.75rem" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
        <strong>Pass #{pass.passNumber} — {pass.trigger}</strong>
        <span className="section-stat">{stat}</span>
      </div>

      <CollapsibleBlock
        title="Raw response"
        defaultOpen={true}
        stat={`${formatNumber((pass.rawResponse || "").length)} chars`}
      >
        <pre className="prompt-content">{pass.rawResponse || "(empty)"}</pre>
      </CollapsibleBlock>

      <CollapsibleBlock title={`Actions emitted (${emitted.length})`}>
        {emitted.length === 0 ? (
          <p className="empty-section">none</p>
        ) : (
          <pre className="prompt-content">{JSON.stringify(emitted, null, 2)}</pre>
        )}
      </CollapsibleBlock>

      <CollapsibleBlock
        title={`Action results (${results.length})`}
        defaultOpen={results.length > 0}
      >
        {results.length === 0 ? (
          <p className="empty-section">no results recorded for this pass</p>
        ) : (
          results.map((r, i) => <ActionResultCard key={i} entry={r} idx={i} />)
        )}
      </CollapsibleBlock>
    </div>
  );
}

function OutputAnalysis() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [hydrated, setHydrated] = useState({}); // id -> full item
  const [hydrating, setHydrating] = useState({});

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get("/output-logs?limit=50");
      setLogs(data.items || []);
    } catch (err) {
      console.error("Failed to fetch output logs:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const toggleExpand = async (log) => {
    if (expandedId === log.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(log.id);

    const needsHydration =
      (log.spillover && (log.spillover.bucket || log.spillover.key)) ||
      log.truncated;
    if (needsHydration && !hydrated[log.id] && !hydrating[log.id]) {
      setHydrating((h) => ({ ...h, [log.id]: true }));
      try {
        const data = await apiClient.get(`/output-logs?id=${encodeURIComponent(log.id)}`);
        if (data && data.item) {
          setHydrated((h) => ({ ...h, [log.id]: data.item }));
        }
      } catch (err) {
        console.error("Failed to hydrate output log:", err);
      } finally {
        setHydrating((h) => ({ ...h, [log.id]: false }));
      }
    }
  };

  if (loading) {
    return <div className="placeholder">Loading output logs...</div>;
  }

  if (logs.length === 0) {
    return (
      <div className="placeholder">
        No output logs yet. Send a message to Anna to generate the first log.
      </div>
    );
  }

  return (
    <div className="output-analysis">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
        <p className="prompt-analysis-description">
          Per-turn capture of every LLM pass Anna ran (raw response, actions emitted
          &amp; their results, latency, tokens). Items larger than ~350 KB spill to S3
          and are hydrated on click.
        </p>
        <RefreshButton onClick={fetchLogs} title="Refresh output logs" />
      </div>

      <div className="prompt-logs-list">
        {logs.map((log) => {
          const isExpanded = expandedId === log.id;
          const full = hydrated[log.id] || log;
          const passes = full.passes || [];
          const passCount = full.passCount || passes.length || (full.hadFollowUp ? 2 : 1);
          const allResults = full.actionsResults || [];

          return (
            <div key={log.id} className={`prompt-log-item ${isExpanded ? "expanded" : ""}`}>
              <div className="prompt-log-header" onClick={() => toggleExpand(log)}>
                <div className="prompt-log-meta">
                  <span className="prompt-log-source">{log.source}</span>
                  <span className="prompt-log-time">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}
                  </span>
                  <span className="prompt-log-session" title={log.sessionId}>
                    {log.sessionId?.slice(0, 16)}
                  </span>
                </div>
                <div className="prompt-log-preview">
                  {log.userMessage?.slice(0, 100)}{log.userMessage?.length > 100 ? "..." : ""}
                </div>
                <div className="prompt-log-tokens">
                  <span className="token-badge total">
                    {passCount} pass{passCount === 1 ? "" : "es"}
                  </span>
                  {log.actionCount > 0 && (
                    <span className="token-badge chat">
                      {log.actionCount} action{log.actionCount > 1 ? "s" : ""}
                    </span>
                  )}
                  {log.convergenceReason && (
                    <span className="token-badge skills">{log.convergenceReason}</span>
                  )}
                  {log.spillover?.bucket && (
                    <span className="token-badge user" title={log.spillover.key}>
                      S3
                    </span>
                  )}
                  {log.truncated && (
                    <span className="token-badge user" style={{ color: "#c33" }}>truncated</span>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="prompt-log-detail">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <span className="section-stat">
                      {hydrating[log.id] ? "Hydrating from S3…" :
                        `${passCount} pass(es) · convergence: ${full.convergenceReason || "—"}`}
                    </span>
                    <button
                      type="button"
                      className="section-tab"
                      onClick={() => copyJson(full)}
                    >
                      Copy as JSON
                    </button>
                  </div>

                  {passes.length === 0 ? (
                    <CollapsibleBlock
                      title="Final assistant response (legacy)"
                      defaultOpen={true}
                      stat={`${formatNumber((full.fullResponse || "").length)} chars`}
                    >
                      <pre className="prompt-content">{full.fullResponse || "(empty)"}</pre>
                      {allResults.length > 0 && (
                        <>
                          <div className="section-header" style={{ marginTop: "0.75rem" }}>
                            <h4>Actions ({allResults.length})</h4>
                          </div>
                          {allResults.map((r, i) => <ActionResultCard key={i} entry={r} idx={i} />)}
                        </>
                      )}
                    </CollapsibleBlock>
                  ) : (
                    <>
                      {passes.map((p, i) => <PassCard key={i} pass={p} />)}
                      <CollapsibleBlock
                        title="Final assistant response"
                        defaultOpen={true}
                        stat={`${formatNumber((full.fullResponse || "").length)} chars`}
                      >
                        <pre className="prompt-content">{full.fullResponse || "(empty)"}</pre>
                      </CollapsibleBlock>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default OutputAnalysis;
